import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Translations object
const translations = {
  en: {
    // Common
    save: 'Save',
    cancel: 'Cancel',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',

    // Navigation
    projects: 'Projects',
    documents: 'Documents',
    parts: 'Parts',
    settings: 'Settings',

    // Profile Menu
    profile: 'Profile',
    changePassword: 'Change Password',
    theme: 'Theme',
    language: 'Language',
    signOut: 'Sign Out',

    // Settings Page
    settingsTitle: 'Settings',
    profileSettings: 'Profile Settings',
    username: 'Username',
    email: 'Email',
    appearance: 'Appearance',
    lightMode: 'Light Mode',
    darkMode: 'Dark Mode',
    languageSettings: 'Language',
    securitySettings: 'Security',
    currentPassword: 'Current Password',
    newPassword: 'New Password',
    confirmPassword: 'Confirm Password',
    updateProfile: 'Update Profile',
    updatePassword: 'Update Password',
    passwordUpdated: 'Password updated successfully',
    profileUpdated: 'Profile updated successfully',
  },
  es: {
    // Common
    save: 'Guardar',
    cancel: 'Cancelar',
    loading: 'Cargando...',
    error: 'Error',
    success: 'Éxito',

    // Navigation
    projects: 'Proyectos',
    documents: 'Documentos',
    parts: 'Piezas',
    settings: 'Configuración',

    // Profile Menu
    profile: 'Perfil',
    changePassword: 'Cambiar Contraseña',
    theme: 'Tema',
    language: 'Idioma',
    signOut: 'Cerrar Sesión',

    // Settings Page
    settingsTitle: 'Configuración',
    profileSettings: 'Configuración del Perfil',
    username: 'Nombre de Usuario',
    email: 'Correo Electrónico',
    appearance: 'Apariencia',
    lightMode: 'Modo Claro',
    darkMode: 'Modo Oscuro',
    languageSettings: 'Idioma',
    securitySettings: 'Seguridad',
    currentPassword: 'Contraseña Actual',
    newPassword: 'Nueva Contraseña',
    confirmPassword: 'Confirmar Contraseña',
    updateProfile: 'Actualizar Perfil',
    updatePassword: 'Actualizar Contraseña',
    passwordUpdated: 'Contraseña actualizada exitosamente',
    profileUpdated: 'Perfil actualizado exitosamente',
  },
  fr: {
    // Common
    save: 'Enregistrer',
    cancel: 'Annuler',
    loading: 'Chargement...',
    error: 'Erreur',
    success: 'Succès',

    // Navigation
    projects: 'Projets',
    documents: 'Documents',
    parts: 'Pièces',
    settings: 'Paramètres',

    // Profile Menu
    profile: 'Profil',
    changePassword: 'Changer le Mot de Passe',
    theme: 'Thème',
    language: 'Langue',
    signOut: 'Déconnexion',

    // Settings Page
    settingsTitle: 'Paramètres',
    profileSettings: 'Paramètres du Profil',
    username: "Nom d'Utilisateur",
    email: 'E-mail',
    appearance: 'Apparence',
    lightMode: 'Mode Clair',
    darkMode: 'Mode Sombre',
    languageSettings: 'Langue',
    securitySettings: 'Sécurité',
    currentPassword: 'Mot de Passe Actuel',
    newPassword: 'Nouveau Mot de Passe',
    confirmPassword: 'Confirmer le Mot de Passe',
    updateProfile: 'Mettre à Jour le Profil',
    updatePassword: 'Mettre à Jour le Mot de Passe',
    passwordUpdated: 'Mot de passe mis à jour avec succès',
    profileUpdated: 'Profil mis à jour avec succès',
  },
  de: {
    // Common
    save: 'Speichern',
    cancel: 'Abbrechen',
    loading: 'Laden...',
    error: 'Fehler',
    success: 'Erfolg',

    // Navigation
    projects: 'Projekte',
    documents: 'Dokumente',
    parts: 'Teile',
    settings: 'Einstellungen',

    // Profile Menu
    profile: 'Profil',
    changePassword: 'Passwort ändern',
    theme: 'Design',
    language: 'Sprache',
    signOut: 'Abmelden',

    // Settings Page
    settingsTitle: 'Einstellungen',
    profileSettings: 'Profileinstellungen',
    username: 'Benutzername',
    email: 'E-Mail',
    appearance: 'Erscheinungsbild',
    lightMode: 'Heller Modus',
    darkMode: 'Dunkler Modus',
    languageSettings: 'Sprache',
    securitySettings: 'Sicherheit',
    currentPassword: 'Aktuelles Passwort',
    newPassword: 'Neues Passwort',
    confirmPassword: 'Passwort Bestätigen',
    updateProfile: 'Profil Aktualisieren',
    updatePassword: 'Passwort Aktualisieren',
    passwordUpdated: 'Passwort erfolgreich aktualisiert',
    profileUpdated: 'Profil erfolgreich aktualisiert',
  },
  ar: {
    // Common
    save: 'حفظ',
    cancel: 'إلغاء',
    loading: 'جاري التحميل...',
    error: 'خطأ',
    success: 'نجاح',

    // Navigation
    projects: 'المشاريع',
    documents: 'المستندات',
    parts: 'الأجزاء',
    settings: 'الإعدادات',

    // Profile Menu
    profile: 'الملف الشخصي',
    changePassword: 'تغيير كلمة المرور',
    theme: 'المظهر',
    language: 'اللغة',
    signOut: 'تسجيل الخروج',

    // Settings Page
    settingsTitle: 'الإعدادات',
    profileSettings: 'إعدادات الملف الشخصي',
    username: 'اسم المستخدم',
    email: 'البريد الإلكتروني',
    appearance: 'المظهر',
    lightMode: 'الوضع الفاتح',
    darkMode: 'الوضع الداكن',
    languageSettings: 'اللغة',
    securitySettings: 'الأمان',
    currentPassword: 'كلمة المرور الحالية',
    newPassword: 'كلمة المرور الجديدة',
    confirmPassword: 'تأكيد كلمة المرور',
    updateProfile: 'تحديث الملف الشخصي',
    updatePassword: 'تحديث كلمة المرور',
    passwordUpdated: 'تم تحديث كلمة المرور بنجاح',
    profileUpdated: 'تم تحديث الملف الشخصي بنجاح',
  },
  zh: {
    // Common
    save: '保存',
    cancel: '取消',
    loading: '加载中...',
    error: '错误',
    success: '成功',

    // Navigation
    projects: '项目',
    documents: '文档',
    parts: '零件',
    settings: '设置',

    // Profile Menu
    profile: '个人资料',
    changePassword: '修改密码',
    theme: '主题',
    language: '语言',
    signOut: '退出登录',

    // Settings Page
    settingsTitle: '设置',
    profileSettings: '个人资料设置',
    username: '用户名',
    email: '电子邮件',
    appearance: '外观',
    lightMode: '浅色模式',
    darkMode: '深色模式',
    languageSettings: '语言',
    securitySettings: '安全',
    currentPassword: '当前密码',
    newPassword: '新密码',
    confirmPassword: '确认密码',
    updateProfile: '更新资料',
    updatePassword: '更新密码',
    passwordUpdated: '密码更新成功',
    profileUpdated: '资料更新成功',
  },
};

// Language labels for display
export const languageLabels = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
  ar: 'العربية',
  zh: '中文',
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const stored = localStorage.getItem('language');
    return stored || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    // Set document direction for RTL languages
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const setLanguage = (lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
    }
  };

  // Translation function
  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const value = {
    language,
    setLanguage,
    t,
    languages: Object.keys(translations),
    languageLabels,
    isRTL: language === 'ar',
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
